import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";
import QuestionMarkIcon from "@mui/icons-material/QuestionMark";
import { VirtualTeam } from "../../../types/models/Team";
import { theme } from "../../../themes/theme";
import { AppState } from "../../../redux/store";
import { useSelector } from "react-redux";
import { TeamMembersState } from "../../../types/models/TeamMembersState";
import { UseTeams } from "../../../hooks/useTeams";
import { TeamMemberProgress, TeamMemberRoleBundle } from "../../../types/models/TeamMember";
import EmptyResult from "../../rules/EmptyResult";
import ProgressPieChart from "../../../components/TableItems/ProgressPieChart";
import PlusMoreLabel from "../../../components/TableItems/PlusMoreLabel";
import MemberModal from "../MemberModal.tsx";
import { MyTeamId } from "..";
import { RuleUnitState } from "../../../types/models/RuleUnitState";
import "./MemberTableStyle.scss";
import { UnreadAlert } from "./UnreadAlert";
import { AcknowledgeAlert } from "./AcknowledgeAlert";
import { RoleState } from "../../../types/models/RoleState";
import { displayUserRole } from "../../../utils/StringUtils";

interface MemberTableProp {
  team?: VirtualTeam;
  allMemberProgress: boolean;
  onOpenEditDialog: (team: VirtualTeam) => void;
  displayTeamProgress: TeamMemberProgress[] | undefined;
  setDisplayTeamProgress: (displayTeamProgress: TeamMemberProgress[] | undefined) => void;
  targetTeamProgress: TeamMemberProgress[] | undefined;
  setTargetTeamProgress: (targetTeamProgress: TeamMemberProgress[] | undefined) => void;
  useTeamsInstance: UseTeams;
}

const MemberTable: React.FC<MemberTableProp> = (prop) => {
  const { team_id, user_id } = useParams();
  const teamState = useSelector<AppState, TeamMembersState>((state) => state.teamMembers);
  const ruleState = useSelector<AppState, RuleUnitState>((state) => state.ruleUnit);
  const roleState = useSelector<AppState, RoleState>((state) => state.role);
  const alerts = ruleState.baseAlertContentRevisionDetails
    ?.map((baseAlert) =>
      baseAlert.RuleInstances?.map((rule) => ({
        Metadata: rule.Metadata,
        RuleInstanceId: rule.RuleInstanceId,
      }))
    )
    .flat();

  const { calculateRulesProgress, filterAlertProgress, getProgressForTeam, isLoading } =
    prop.useTeamsInstance;

  const [selectedMember, setSelectedMember] = useState<TeamMemberProgress | undefined>();
  const [openMemberDetail, setOpenMemberDetail] = useState<boolean>(false);

  const [orderDirections, setOrderDirections] = useState<Array<"asc" | "desc">>([
    "asc",
    "asc",
    "asc",
    "asc"
  ]);
  const [sortFlags, setSortFlags] = useState<Array<boolean>>([false, false, false, false]);

  const handleSortRequest = (index: number) => {
    if (!prop.displayTeamProgress) return;

    // Display sort allow
    const newSortFlags: Array<boolean> = JSON.parse(JSON.stringify(sortFlags));
    for (let i = 0; i < newSortFlags.length; i++) {
      newSortFlags[i] = i === index;
    }
    setSortFlags(newSortFlags);

    // Change allow direction
    const newOrderDirections: Array<"asc" | "desc"> = JSON.parse(JSON.stringify(orderDirections));
    newOrderDirections[index] = newOrderDirections[index] === "asc" ? "desc" : "asc";
    setOrderDirections(newOrderDirections);

    // Sort data
    const forward = newOrderDirections[index] === "asc" ? 1 : -1;
    const behind = newOrderDirections[index] === "asc" ? -1 : 1;
    const sortedMembers = prop.displayTeamProgress.sort((a, b) => {
      let percentA = 0;
      let percentB = 0;
      if (index === 2 && a.completion && b.completion) {
        percentA = a.completion.total == 0 ? 100 : a.completion.completed / a.completion.total;
        percentB = b.completion.total == 0 ? 100 : b.completion.completed / b.completion.total;
      }

      switch (index) {
        case 0:
          return (a.name?.toLowerCase() ?? "") > (b.name?.toLowerCase() ?? "") ? forward : behind;
        case 1:
          return (a.roles?.join().toLowerCase() ?? "") > (b.roles?.join().toLowerCase() ?? "")
            ? forward
            : behind;
        case 2:
          return getLocationNames(a.locations ?? [])
            .join()
            .toLowerCase() >
            getLocationNames(b.locations ?? [])
              .join()
              .toLowerCase()
            ? forward
            : behind;
        case 3:
          // The question mark is always last
          if (!a.alertCount || a.alertCount === 0) {
            return behind;
          } else if (!b.alertCount || b.alertCount === 0) {
            return forward;
          } else {
            return percentA > percentB ? forward : behind;
          }
        case 4:
          return (a.roleBundles?.map(bundle => bundle.name).join().toLowerCase() ?? "") > (b.roleBundles?.map(bundle => bundle.name).join().toLowerCase() ?? "")
            ? forward
            : behind;
        default:
          return 0;
      }
    });

    prop.setDisplayTeamProgress(sortedMembers);
  };

  useEffect(() => {
    // Display selected team
    if (prop.team && prop.team.TeamId !== MyTeamId) {
      // Clear progress to avoid flashing display for previous progress
      prop.setDisplayTeamProgress(undefined);
      getProgressForTeam(prop.team.TeamId, (progress) => {
        prop.setTargetTeamProgress(progress.sort((a, b) => a.name.localeCompare(b.name)));
        openUserModalForURLParam(progress);
      });
      clearSortingStates();
    }
  }, [prop.team]);

  const clearSortingStates = () => {
    // Initialise sorting states
    setOrderDirections(["asc", "asc", "asc", "asc"]);
    setSortFlags([false, false, false, false]);
  };

  useEffect(() => {
    // Display "My Team"
    if (prop.allMemberProgress && teamState.orgProgress && !prop.team) {
      prop.setTargetTeamProgress(teamState.orgProgress);
      openUserModalForURLParam(teamState.orgProgress);
      clearSortingStates();
    }
  }, [teamState.orgProgress, prop.allMemberProgress]);

  useEffect(() => {
    // Calculate pie chart values
    if (prop.targetTeamProgress && ruleState.baseAlertRules) {
      const progress = [...prop.targetTeamProgress];
      progress.forEach((member) => {
        const progressCount = calculateRulesProgress(
          member.progress ?? [],
          member.roles ?? [],
          member.locations ?? []
        );
        member.completion = { completed: progressCount.completed, total: progressCount.total };
      });

      prop.setDisplayTeamProgress(progress);
    }
  }, [prop.targetTeamProgress, ruleState.baseAlertRules]);

  const openUserModalForURLParam = (progress: TeamMemberProgress[] | undefined) => {
    if (!progress) return;

    // Open member's modal if the user id is specified in URL
    if (
      team_id &&
      user_id &&
      (prop.team?.TeamId === team_id || (team_id === MyTeamId && prop.allMemberProgress))
    ) {
      const user = progress.find((member) => member.user_id === user_id);
      if (user) {
        setSelectedMember(user);
        setOpenMemberDetail(true);
      }
    }
  };

  const SkeletonRow = () => {
    return (
      <TableRow>
        {Array.from({ length: 4 }, (_, i) => (
          <TableCell key={i} height={"80px"}>
            <Skeleton variant="text" height="1.5em" />
          </TableCell>
        ))}
        <TableCell key={"alert"} height={"80px"}>
          <Skeleton variant="circular" height="2.5em" width="2.5em" />
        </TableCell>
      </TableRow>
    );
  };

  const createRolesCell = (roles: string[]) => {
    const convertedRoles =
      roles?.map((role) => displayUserRole(role, roleState.knownRoles?.roles)) ?? [];
    if (convertedRoles.length > 2) {
      const firstRole = convertedRoles[0];
      return (
        <Box sx={{ display: "flex", alignItems: "flex-end" }}>
          {firstRole}
          <PlusMoreLabel moreCount={convertedRoles.length - 1} />
        </Box>
      );
    } else {
      return convertedRoles.join(", ");
    }
  };

  const createRoleBundlesCell = (roleBundles: TeamMemberRoleBundle[]) => {
    if (roleBundles.length > 2) {
      const firstRoleBundle = roleBundles[0];
      return (
        <Box sx={{ display: "flex", alignItems: "flex-end" }}>
          {firstRoleBundle.name}
          <PlusMoreLabel moreCount={roleBundles.length - 1} />
        </Box>
      );
    } else {
      return roleBundles.map((bundle) => bundle.name).join(", ");
    }
  };

  const createPlusMore = (values: string[]) => {
    if (values.length > 1) {
      const firstValue = values[0];
      return (
        <Box sx={{ display: "flex", alignItems: "flex-end" }}>
          {firstValue}
          <PlusMoreLabel moreCount={values.length - 1} />
        </Box>
      );
    } else {
      return values.join(", ");
    }
  };

  const getLocationNames = (locationIds: string[]) => {
    const locationNames: string[] = [];
    const knownLocations = roleState.knownRoles?.locations;

    locationIds.forEach((id) => {
      const matchedId = knownLocations?.find((location) => location.LNIId === id);
      if (matchedId) {
        locationNames.push(`${matchedId.LNIId} - ${matchedId.Name}`);
      }
    });
    return locationNames;
  };

  const createRuleProgressPieChart = (member: TeamMemberProgress) => {
    if (!member.alertCount) {
      // Unknown person who is in Team but not in Organisation
      return (
        <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
          <Box
            sx={{
              display: "flex",
              height: "50px",
              width: "50px",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <QuestionMarkIcon sx={{ mt: 0, height: "32px", width: "46px", color: "lightgray" }} />
          </Box>
        </Box>
      );
    } else {
      return (
        <Box sx={{ position: "relative" }}>
          <ProgressPieChart
            total={member.alertCount}
            highlighted={member.overdueCount}
            completed={member.acknowledgedCount}
            modal={false}
          />
          {member.alertCount - member.acknowledgedCount > 0 &&
          member.alertCount - member.acknowledgedCount < 10 ? (
            <span className="not-acknowledged-small-number">
              {member.alertCount - member.acknowledgedCount}
            </span>
          ) : (
            <span className="not-acknowledged-number">
              {member.alertCount - member.acknowledgedCount}
            </span>
          )}
        </Box>
      );
    }
  };

  const getOverdueAlerts = (member: TeamMemberProgress) => {
    if (member.overdueCount > 0) {
      return <AcknowledgeAlert count={member.overdueCount} />;
    }
    return null;
  };

  const onClickMember = (member: TeamMemberProgress) => {
    setSelectedMember(member);
    setOpenMemberDetail(true);
  };

  return (
    <>
      <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
        <Table size="small">
          <TableHead sx={{ backgroundColor: theme.table.subHeaderBgColor }}>
            <TableRow>
              <TableCell onClick={() => handleSortRequest(0)}>
                <TableSortLabel active={sortFlags[0]} direction={orderDirections[0]}>
                  Name
                </TableSortLabel>
              </TableCell>
              <TableCell onClick={() => handleSortRequest(4)}>
                <TableSortLabel active={sortFlags[4]} direction={orderDirections[4]}>
                  Role Bundles
                </TableSortLabel>
              </TableCell>
              <TableCell onClick={() => handleSortRequest(1)}>
                <TableSortLabel active={sortFlags[1]} direction={orderDirections[1]}>
                  Roles
                </TableSortLabel>
              </TableCell>
              {/*TODO: Update sort index when location is available */}
              <TableCell onClick={() => handleSortRequest(2)}>
                <TableSortLabel active={sortFlags[2]} direction={orderDirections[2]}>
                  Locations
                </TableSortLabel>
              </TableCell>
              <TableCell align="center" width={"120px"} onClick={() => handleSortRequest(3)}>
                <TableSortLabel active={sortFlags[3]} direction={orderDirections[3]}>
                  Alerts
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody
            sx={{
              "& .MuiTableRow-root:hover": {
                backgroundColor: theme.table.hoverBgColor,
              },
            }}
          >
            {isLoading || !prop.displayTeamProgress ? (
              <>
                <SkeletonRow />
                <SkeletonRow />
              </>
            ) : prop.displayTeamProgress?.length === 0 ? (
              <TableRow>
                <TableCell sx={{ p: 0 }} colSpan={8}>
                  <EmptyResult>No members available</EmptyResult>
                </TableCell>
              </TableRow>
            ) : (
              prop.displayTeamProgress?.map((member, index) => (
                <TableRow
                  key={member.user_id + member.email + index}
                  hover
                  onClick={() => onClickMember(member)}
                >
                  <TableCell>
                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                      {member.name ? member.name : member.email}
                      <Typography sx={{ fontSize: 13, color: theme.table.subTextColor }}>
                        {member.email}
                      </Typography>
                      <Box sx={{ mt: 1, mb: 0.5 }}>{getOverdueAlerts(member)}</Box>
                      <Box sx={{ mt: 1, mb: 0.5 }}>{member.unreadMessage && <UnreadAlert />}</Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {createRoleBundlesCell(member.roleBundles ?? []) ? (
                      createRoleBundlesCell(member.roleBundles ?? [])
                    ) : (
                      <Typography sx={{ fontSize: 13, color: theme.table.subTextColor }}>
                        No role bundles assigned
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {createRolesCell(member.roles ?? []) ? (
                      createRolesCell(member.roles ?? [])
                    ) : (
                      <Typography sx={{ fontSize: 13, color: theme.table.subTextColor }}>
                        No roles assigned
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {createPlusMore(getLocationNames(member.locations ?? [])) ? (
                      createPlusMore(getLocationNames(member.locations ?? []))
                    ) : (
                      <Typography sx={{ fontSize: 13, color: theme.table.subTextColor }}>
                        No locations assigned
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{createRuleProgressPieChart(member)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {selectedMember && (
        <MemberModal
          onClose={() => setOpenMemberDetail(false)}
          open={openMemberDetail}
          user={selectedMember}
          userProgress={filterAlertProgress(
            selectedMember.progress ?? [],
            selectedMember.roles ?? [],
            selectedMember.locations ?? []
          )}
          selectedMember={selectedMember}
          setSelectedMember={setSelectedMember}
          currentTeam={prop.team}
          alerts={alerts}
        />
      )}
    </>
  );
};

export default MemberTable;
